import type { AuthState } from '../../types';

import { useMemo, useEffect } from 'react';
import { useSetState } from 'minimal-shared/hooks';

import { usePartnerInfo } from 'src/actions/partner/usePartner';

import { toast } from 'src/components/snackbar';

import { isValidToken } from './utils';
import { JWT_STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';

// ----------------------------------------------------------------------

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true });
  
  // 토큰이 있고 유효한 경우에만 API 호출
  const accessToken = sessionStorage.getItem(JWT_STORAGE_KEY);
  const hasValidToken = accessToken && isValidToken(accessToken);
  
  const { data: partnerInfo, error, isLoading } = usePartnerInfo({
    enabled: hasValidToken || false,
  });

  useEffect(() => {
    // 토큰이 없거나 유효하지 않은 경우
    if (!hasValidToken) {
      sessionStorage.removeItem(JWT_STORAGE_KEY);
      setState({ user: null, loading: false });
      return;
    }

    // API 에러가 발생한 경우
    if (error) {
      console.error('Partner info fetch error:', error);
      sessionStorage.removeItem(JWT_STORAGE_KEY);
      setState({ user: null, loading: false });
      
      // 에러 메시지를 Snackbar로 표시
      toast.error('인증 오류', {
        description: error.message,
      });
      
      return;
    }

    if (partnerInfo) {
      setState({ user: { ...partnerInfo }, loading: false });
    } else if (!isLoading) {
      setState({ user: null, loading: false });
    }
  }, [partnerInfo, error, isLoading, hasValidToken, setState]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user ? { ...state.user, role: state.user?.role ?? 'admin' } : null,
      partnerInfo,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [partnerInfo, state.user, status]
  );

  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
